import { Component, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  AccessControlService,
  AppUserProfile,
  AppUserService,
  AttachmentService,
  AuthService,
  DashboardPeriodService,
  SubscriptionService,
  SubscriptionUsageSummary
} from '../core/services';
import { TranslatePipe } from '../core/pipes/translate.pipe';
import {
  ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE,
  APP_OTP_EXPIRY_SECONDS,
  ATTACHMENT_MAX_FILE_COUNT_BY_FEATURE,
  FeatureTypeEnum
} from '../core/constants/app-constants';
import { MONTH_TRANSLATION_KEYS } from '../core/i18n/app-translations';
import {
  combineBangladeshMobileNumber,
  extractBangladeshMobileLocalPart,
  normalizeBangladeshMobileLocalInput,
  isValidBangladeshMobileLocalPart,
  BANGLADESH_COUNTRY_CODE
} from '../core/utils/bd-mobile.util';
import { ImageCropModalComponent } from '../core/ui/image-crop-modal/image-crop-modal.component';
import { LanguageSwitchComponent } from '../core/ui/language-switch/language-switch.component';

@Component({
  selector: 'app-top-header',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, ImageCropModalComponent, LanguageSwitchComponent],
  templateUrl: './top-header.html',
  styleUrl: './top-header.css',
})
export class TopHeader implements OnDestroy {
  readonly mobilePrefix = BANGLADESH_COUNTRY_CODE;
  @Input() showMonthControl = true;
  @Input() pageTitle = '';
  @Input() showPageInfoButton = false;
  @Output() pageInfoClick = new EventEmitter<void>();

  isCalendarOpen = false;
  isUserOptionOpen = false;
  isImageUpdateModalOpen = false;
  isProfileEditModalOpen = false;

  readonly defaultProfileSvg = '/profile-placeholder.svg';

  userImage = this.defaultProfileSvg;
  profile: AppUserProfile | null = null;
  usageSummary: SubscriptionUsageSummary | null = null;

  basicInfoMessage: string | null = null;
  basicInfoError: string | null = null;
  emailUpdateMessage: string | null = null;
  emailUpdateError: string | null = null;
  passwordUpdateMessage: string | null = null;
  passwordUpdateError: string | null = null;
  profileError: string | null = null;
  profileSuccess: string | null = null;
  profileImageCropSourceFile: File | null = null;

  isSavingBasicInfo = false;
  isSendingNewEmailOtp = false;
  isUpdatingEmail = false;
  isUpdatingPassword = false;
  isUploadingImage = false;
  isEmailOtpSent = false;
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmNewPassword = false;

  showDashboardMonthControl = false;
  selectedMonth: number = new Date().getMonth();
  selectedYear: number = new Date().getFullYear();
  months = [...MONTH_TRANSLATION_KEYS];
  availableYears = Array.from({ length: 16 }).map((_, i) => new Date().getFullYear() - 5 + i);
  draftMonth: number = this.selectedMonth;
  draftYear: number = this.selectedYear;
  currentUrl = '';
  isDesktopHeader = typeof window !== 'undefined' ? window.innerWidth > 900 : true;

  basicInfoForm = {
    name: '',
    organizationName: '',
    mobileNumber: ''
  };

  emailUpdateForm = {
    newEmail: '',
    newEmailOtp: ''
  };

  passwordUpdateForm = {
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  };

  newEmailOtpExpiresInSeconds = 0;
  newEmailOtpTarget = '';
  private newEmailOtpCountdownSub: Subscription | null = null;
  private summaryRefreshSubscription: Subscription;

  constructor(
    private readonly router: Router,
    private readonly auth: AuthService,
    private readonly access: AccessControlService,
    private readonly appUsers: AppUserService,
    private readonly attachments: AttachmentService,
    private readonly dashboardPeriod: DashboardPeriodService,
    private readonly subscriptionService: SubscriptionService
  ) {
    const period = this.dashboardPeriod.current;
    this.selectedMonth = period.month;
    this.selectedYear = period.year;
    this.draftMonth = period.month;
    this.draftYear = period.year;
    this.currentUrl = this.router.url;
    this.updateViewportMode();
    this.updateMonthControlVisibility(this.router.url);
    this.loadCurrentUser();
    this.summaryRefreshSubscription = this.subscriptionService.summaryRefresh$.subscribe(() => {
      if (this.showStorageUsage) {
        this.loadUsageSummary();
      }
    });
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl = event.urlAfterRedirects;
        this.updateMonthControlVisibility(event.urlAfterRedirects);
      });
  }

  get resolvedPageTitle(): string {
    const customTitle = (this.pageTitle || '').trim();
    if (customTitle) {
      return customTitle;
    }

    const url = (this.currentUrl || '').toLowerCase();
    if (url.startsWith('/dashboard')) return 'page.dashboard';
    if (url.startsWith('/payment')) return 'page.transaction';
    if (url.startsWith('/expense')) return 'page.expense';
    if (url.startsWith('/renter')) return 'page.renter';
    if (url.startsWith('/house')) return 'page.house';
    if (url.startsWith('/deleted')) return 'page.deleted';
    if (url.startsWith('/user')) return 'page.other';
    if (url.startsWith('/activity')) return 'page.activity';
    return 'page.dashboard';
  }

  get newEmailOtpExpireLabel(): string {
    return this.formatCountdown(this.newEmailOtpExpiresInSeconds);
  }

  get desktopHeaderRowStyle(): Record<string, string> | null {
    if (!this.isDesktopHeader) {
      return null;
    }
    return {
      position: 'relative',
      display: 'block',
      minHeight: '40px'
    };
  }

  get desktopHeaderLeftStyle(): Record<string, string> | null {
    if (!this.isDesktopHeader) {
      return null;
    }
    return {
      position: 'absolute',
      left: '0',
      top: '50%',
      transform: 'translateY(-50%)',
      minWidth: '0',
      width: 'auto',
      maxWidth: 'calc(100% - 220px)'
    };
  }

  get desktopHeaderCenterStyle(): Record<string, string> | null {
    if (!this.isDesktopHeader) {
      return null;
    }
    return {
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)'
    };
  }

  get desktopHeaderRightStyle(): Record<string, string> | null {
    if (!this.isDesktopHeader) {
      return null;
    }
    return {
      position: 'absolute',
      right: '0',
      top: '50%',
      transform: 'translateY(-50%)'
    };
  }

  openCalendarModal() {
    this.draftMonth = this.selectedMonth;
    this.draftYear = this.selectedYear;
    this.isCalendarOpen = true;
  }

  onPageInfoClick(event: MouseEvent): void {
    event.stopPropagation();
    this.pageInfoClick.emit();
  }

  closeCalendarModal(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.isCalendarOpen = false;
    }
  }

  closeByIcon() {
    this.isCalendarOpen = false;
  }

  onMonthSelected(event: { month: number, year: number }) {
    this.selectedMonth = event.month;
    this.selectedYear = event.year;
    this.isCalendarOpen = false;
    this.dashboardPeriod.setPeriod({ month: this.selectedMonth, year: this.selectedYear });
  }

  applyDraftMonth(): void {
    this.onMonthSelected({
      month: Number(this.draftMonth),
      year: Number(this.draftYear)
    });
  }

  openUserMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isUserOptionOpen = !this.isUserOptionOpen;
  }

  openImageUpdateModal(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.canManageProfileImage) {
      return;
    }
    this.isUserOptionOpen = false;
    this.profileError = null;
    this.profileSuccess = null;
    this.isImageUpdateModalOpen = true;
  }

  openProfileEditModal(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.isUserOptionOpen = false;
    this.resetProfileModalState();
    this.basicInfoForm = {
      name: `${this.profile?.name ?? ''}`.trim(),
      organizationName: `${this.profile?.organizationName ?? ''}`.trim(),
      mobileNumber: extractBangladeshMobileLocalPart(this.profile?.mobileNumber)
    };
    this.emailUpdateForm = {
      newEmail: '',
      newEmailOtp: ''
    };
    this.passwordUpdateForm = {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    };
    this.isEmailOtpSent = false;
    this.showCurrentPassword = false;
    this.showNewPassword = false;
    this.showConfirmNewPassword = false;
    this.isProfileEditModalOpen = true;
  }

  closeImageUpdateModal(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.isImageUpdateModalOpen = false;
    this.profileImageCropSourceFile = null;
    this.profileError = null;
    this.profileSuccess = null;
  }

  closeProfileEditModal(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.isProfileEditModalOpen = false;
  }

  saveBasicInfo(): void {
    const profileId = Number(this.profile?.id);
    const name = `${this.basicInfoForm.name ?? ''}`.trim();
    const organizationName = `${this.basicInfoForm.organizationName ?? ''}`.trim();
    this.normalizeBasicInfoMobile();
    const mobileNumber = `${this.basicInfoForm.mobileNumber ?? ''}`.trim();

    if (!Number.isFinite(profileId)) {
      this.basicInfoError = 'User profile not loaded.';
      return;
    }

    if (!name) {
      this.basicInfoError = 'Full name is required.';
      return;
    }
    if (!organizationName) {
      this.basicInfoError = 'Organization/Housing name is required.';
      return;
    }
    if (!isValidBangladeshMobileLocalPart(mobileNumber)) {
      this.basicInfoError = 'Enter a valid mobile number.';
      return;
    }

    this.basicInfoError = null;
    this.basicInfoMessage = null;
    this.isSavingBasicInfo = true;

    this.appUsers.updateBasicProfile({
      id: profileId,
      name,
      organizationName,
      mobileNumber: combineBangladeshMobileNumber(mobileNumber) || undefined
    }).subscribe({
      next: (res: any) => {
        if (res?.code && `${res.code}` !== '000') {
          this.basicInfoError = `${res?.message ?? 'Failed to update basic info.'}`;
          return;
        }
        this.basicInfoMessage = `${res?.message ?? 'Basic info updated successfully.'}`;
        this.loadCurrentUser(true);
      },
      error: (err: any) => {
        this.basicInfoError = `${err?.error?.message ?? err?.message ?? 'Failed to update basic info.'}`;
      },
      complete: () => {
        this.isSavingBasicInfo = false;
      }
    });
  }

  sendOldEmailOtp(): void {
    this.sendNewEmailOtp();
  }

  sendNewEmailOtp(): void {
    const profileId = Number(this.profile?.id);
    const newEmail = `${this.emailUpdateForm.newEmail ?? ''}`.trim().toLowerCase();
    if (!Number.isFinite(profileId)) {
      this.emailUpdateError = 'User profile not loaded.';
      return;
    }
    if (!this.isValidEmail(newEmail)) {
      this.emailUpdateError = 'Enter a valid new email address.';
      return;
    }

    this.emailUpdateError = null;
    this.emailUpdateMessage = null;
    this.isSendingNewEmailOtp = true;

    this.appUsers.sendNewEmailChangeOtp(newEmail, '', profileId).subscribe({
      next: (res: any) => {
        if (res?.code && `${res.code}` !== '000') {
          this.emailUpdateError = `${res?.message ?? 'Failed to send OTP to new email.'}`;
          return;
        }
        this.isEmailOtpSent = true;
        this.emailUpdateForm.newEmailOtp = '';
        this.newEmailOtpTarget = '';
        this.startNewEmailOtpCountdown();
        this.emailUpdateMessage = null;
      },
      error: (err: any) => {
        this.emailUpdateError = `${err?.error?.message ?? err?.message ?? 'Failed to send OTP to new email.'}`;
      },
      complete: () => {
        this.isSendingNewEmailOtp = false;
      }
    });
  }

  updateEmail(): void {
    const profileId = Number(this.profile?.id);
    const newEmail = `${this.emailUpdateForm.newEmail ?? ''}`.trim().toLowerCase();
    const newEmailOtp = `${this.emailUpdateForm.newEmailOtp ?? ''}`.trim();

    if (!Number.isFinite(profileId)) {
      this.emailUpdateError = 'User profile not loaded.';
      return;
    }
    if (!this.isValidEmail(newEmail)) {
      this.emailUpdateError = 'Enter a valid new email address.';
      return;
    }
    if (newEmail === `${this.profile?.email ?? ''}`.trim().toLowerCase()) {
      this.emailUpdateError = 'New email must be different from current email.';
      return;
    }
    if (!/^[0-9]{4,8}$/.test(newEmailOtp)) {
      this.emailUpdateError = 'Enter a valid OTP from new email.';
      return;
    }
    if (this.newEmailOtpExpiresInSeconds === 0) {
      this.emailUpdateError = 'New email OTP expired. Send it again.';
      return;
    }

    this.emailUpdateError = null;
    this.emailUpdateMessage = null;
    this.isUpdatingEmail = true;

    this.appUsers.updateProfileEmail(newEmail, newEmailOtp, profileId).subscribe({
      next: (res: any) => {
        if (res?.code && `${res.code}` !== '000') {
          this.emailUpdateError = `${res?.message ?? 'Failed to update email.'}`;
          return;
        }
        this.emailUpdateMessage = `${res?.message ?? 'Email updated successfully.'}`;
        this.emailUpdateForm = {
          newEmail: '',
          newEmailOtp: ''
        };
        this.loadCurrentUser(true);
        this.isEmailOtpSent = false;
        this.newEmailOtpExpiresInSeconds = 0;
        setTimeout(() => this.logout(), 800);
      },
      error: (err: any) => {
        this.emailUpdateError = `${err?.error?.message ?? err?.message ?? 'Failed to update email.'}`;
      },
      complete: () => {
        this.isUpdatingEmail = false;
      }
    });
  }

  updatePassword(): void {
    const profileId = Number(this.profile?.id);
    const currentPassword = `${this.passwordUpdateForm.currentPassword ?? ''}`;
    const newPassword = `${this.passwordUpdateForm.newPassword ?? ''}`;
    const confirmNewPassword = `${this.passwordUpdateForm.confirmNewPassword ?? ''}`;

    if (!Number.isFinite(profileId)) {
      this.passwordUpdateError = 'User profile not loaded.';
      return;
    }
    if (!currentPassword) {
      this.passwordUpdateError = 'Current password is required.';
      return;
    }
    if (!newPassword) {
      this.passwordUpdateError = 'New password is required.';
      return;
    }
    if (newPassword.length < 6) {
      this.passwordUpdateError = 'New password must be at least 6 characters.';
      return;
    }
    if (currentPassword === newPassword) {
      this.passwordUpdateError = 'New password must be different from current password.';
      return;
    }
    if (!confirmNewPassword) {
      this.passwordUpdateError = 'Retype new password.';
      return;
    }
    if (newPassword !== confirmNewPassword) {
      this.passwordUpdateError = 'New passwords do not match.';
      return;
    }

    this.passwordUpdateError = null;
    this.passwordUpdateMessage = null;
    this.isUpdatingPassword = true;

    this.appUsers.updatePassword(currentPassword, newPassword, profileId).subscribe({
      next: (res: any) => {
        if (res?.code && `${res.code}` !== '000') {
          this.passwordUpdateError = `${res?.message ?? 'Failed to update password.'}`;
          return;
        }
        this.passwordUpdateMessage = `${res?.message ?? 'Password updated successfully.'}`;
        this.passwordUpdateForm = {
          currentPassword: '',
          newPassword: '',
          confirmNewPassword: ''
        };
        setTimeout(() => this.logout(), 800);
      },
      error: (err: any) => {
        this.passwordUpdateError = `${err?.error?.message ?? err?.message ?? 'Failed to update password.'}`;
      },
      complete: () => {
        this.isUpdatingPassword = false;
      }
    });
  }

  toggleCurrentPasswordVisibility(): void {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmNewPasswordVisibility(): void {
    this.showConfirmNewPassword = !this.showConfirmNewPassword;
  }

  normalizeBasicInfoMobile(): void {
    this.basicInfoForm.mobileNumber = normalizeBangladeshMobileLocalInput(this.basicInfoForm.mobileNumber);
  }

  onProfileImageSelected(event: Event): void {
    if (!this.canManageProfileImage) {
      return;
    }

    const input = event.target as HTMLInputElement | null;
    const file = (input?.files ?? [])[0];
    if (!file) {
      return;
    }

    if (!Number.isFinite(this.profileImageFeatureId)) {
      this.profileError = 'User profile not loaded.';
      if (input) {
        input.value = '';
      }
      return;
    }

    const allowedExtensions = ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE[this.profileImageFeatureType];
    const name = `${file.name ?? ''}`.toLowerCase();
    if (!allowedExtensions.some((ext) => name.endsWith(ext))) {
      this.profileError = `Only ${this.allowedImageExtensionsText} files are allowed.`;
      if (input) {
        input.value = '';
      }
      return;
    }

    this.profileError = null;
    this.profileSuccess = null;
    this.profileImageCropSourceFile = file;

    if (input) {
      input.value = '';
    }
  }

  onProfileImageCropCancelled(): void {
    this.profileImageCropSourceFile = null;
  }

  onProfileImageCropped(file: File): void {
    this.profileImageCropSourceFile = null;
    this.uploadProfileImage(file);
  }

  logout(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.isUserOptionOpen = false;
    this.isImageUpdateModalOpen = false;
    this.profileImageCropSourceFile = null;
    this.isProfileEditModalOpen = false;
    this.attachments.clearPreviewUrlCache();
    this.appUsers.clearCurrentProfileCache();
    this.auth.logout();
    this.access.clear();
    this.router.navigate(['/signIn']);
  }

  onUserImageError(): void {
    this.userImage = this.defaultProfileSvg;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.isUserOptionOpen = false;
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const isInsideImageUpdateModal = !!target?.closest('.image-update-modal');
    const isInsideCropOverlay = !!target?.closest('.crop-overlay');
    if (this.isImageUpdateModalOpen && !isInsideImageUpdateModal && !isInsideCropOverlay) {
      this.isImageUpdateModalOpen = false;
      this.profileImageCropSourceFile = null;
    }
    if (this.isProfileEditModalOpen && !target?.closest('.profile-edit-modal')) {
      this.isProfileEditModalOpen = false;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportMode();
  }

  ngOnDestroy(): void {
    this.summaryRefreshSubscription.unsubscribe();
    this.newEmailOtpCountdownSub?.unsubscribe();
    this.profileImageCropSourceFile = null;
  }

  get showStorageUsage(): boolean {
    return Number(this.profile?.role) !== 4 && !!this.usageSummary;
  }

  get storageUsagePercent(): number {
    const used = Number(this.usageSummary?.usage?.usedFileSize ?? 0);
    const total = this.totalStorageBytes;
    if (total <= 0) {
      return 0;
    }
    return Math.min(100, Math.max(0, (used / total) * 100));
  }

  get totalStorageBytes(): number {
    const totalMb = Number(this.usageSummary?.currentPackage?.maxFileStorage ?? 0);
    if (!Number.isFinite(totalMb) || totalMb <= 0) {
      return 0;
    }
    return totalMb * 1024 * 1024;
  }

  get storageUsagePercentLabel(): string {
    return `${Math.round(this.storageUsagePercent)}%`;
  }

  get packageExpireDateLabel(): string | null {
    const rawDate = `${this.usageSummary?.currentPackage?.expireDate ?? ''}`.trim();
    if (!rawDate) {
      return null;
    }
    const [yearText, monthText, dayText] = rawDate.split('-');
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return rawDate;
    }
    const parsed = new Date(year, month - 1, day);
    if (Number.isNaN(parsed.getTime())) {
      return rawDate;
    }
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(parsed);
  }

  formatStorage(sizeInBytes: number | null | undefined): string {
    const size = Number(sizeInBytes ?? 0);
    if (!Number.isFinite(size) || size <= 0) {
      return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = size;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    const digits = value >= 100 || unitIndex === 0 ? 0 : value >= 10 ? 1 : 2;
    return `${value.toFixed(digits)} ${units[unitIndex]}`;
  }

  private updateMonthControlVisibility(url: string): void {
    this.showDashboardMonthControl = url.startsWith('/dashboard');
    if (!this.showDashboardMonthControl) {
      this.isCalendarOpen = false;
    }
  }

  private updateViewportMode(): void {
    if (typeof window === 'undefined') {
      this.isDesktopHeader = true;
      return;
    }
    this.isDesktopHeader = window.innerWidth > 900;
  }

  private loadCurrentUser(forceRefresh = false): void {
    this.appUsers.me(forceRefresh).subscribe({
      next: (user) => {
        this.profile = user;
        this.resolveUserImage(user);
        if (Number(user?.role) === 4) {
          this.usageSummary = null;
          return;
        }
        this.loadUsageSummary();
      },
      error: () => {
        this.profile = null;
        this.usageSummary = null;
        this.userImage = this.defaultProfileSvg;
      }
    });
  }

  private loadUsageSummary(): void {
    this.subscriptionService.getSubscriptionSummary().subscribe({
      next: (summary) => {
        this.usageSummary = summary;
      },
      error: () => {
        this.usageSummary = null;
      }
    });
  }

  private resolveUserImage(user: AppUserProfile | null): void {
    const attachmentId = Number(user?.attachments?.[0]?.id);
    if (!Number.isFinite(attachmentId)) {
      this.userImage = this.defaultProfileSvg;
      return;
    }

    this.attachments.previewUrlByAttachmentIdCached(attachmentId).subscribe({
      next: (url: string) => {
        this.userImage = url || this.defaultProfileSvg;
      },
      error: () => {
        this.userImage = this.defaultProfileSvg;
      }
    });
  }

  private uploadProfileImage(file: File): void {
    const targetFeatureId = this.profileImageFeatureId;
    if (!Number.isFinite(targetFeatureId)) {
      this.profileError = 'User profile not loaded.';
      return;
    }

    const existingAttachmentIds = Array.isArray(this.profile?.attachments)
      ? this.profile.attachments
          .map((item) => Number(item?.id))
          .filter((idValue) => Number.isFinite(idValue))
      : [];

    this.isUploadingImage = true;
    this.profileError = null;
    this.profileSuccess = null;

    this.attachments.manageAttachment({
      featureType: this.profileImageFeatureType,
      featureId: targetFeatureId,
      actionType: 2,
      files: [file],
      deletingIds: existingAttachmentIds,
      service: this.profileImageCreateService
    }).subscribe({
      next: () => {
        this.profileSuccess = 'Profile image updated successfully.';
        this.attachments.clearPreviewUrlCache();
        this.loadCurrentUser(true);
        this.isImageUpdateModalOpen = false;
        this.profileImageCropSourceFile = null;
      },
      error: (err: any) => {
        this.profileError = err?.message || 'Failed to upload profile image.';
        this.isUploadingImage = false;
      },
      complete: () => {
        this.isUploadingImage = false;
      }
    });
  }

  get profileImageFeatureType(): FeatureTypeEnum {
    return this.profile?.role === 4
      ? FeatureTypeEnum.RENTER_IMAGE
      : FeatureTypeEnum.ORGANIZATION_LOGO_OR_EMPLOYEE_IMAGE;
  }

  get profileImageFeatureId(): number {
    if (this.profile?.role === 4) {
      return Number(this.profile?.renterId);
    }
    return Number(this.profile?.id);
  }

  get allowedImageExtensionsText(): string {
    return ATTACHMENT_ALLOWED_EXTENSIONS_BY_FEATURE[this.profileImageFeatureType].join(', ');
  }

  get profileImageMaxFileCount(): number {
    return ATTACHMENT_MAX_FILE_COUNT_BY_FEATURE[this.profileImageFeatureType];
  }

  get profileImageCreateService(): string {
    return this.profile?.role === 4 ? 'RENTER_IMAGE_CREATE' : 'ORGANIZATION_LOGO_OR_EMPLOYEE_IMAGE';
  }

  get canShowFullProfileEdit(): boolean {
    const role = Number(this.profile?.role);
    return role === 1 || role === 2;
  }

  get canManageProfileImage(): boolean {
    const role = Number(this.profile?.role);
    return role === 1 || role === 2;
  }

  get profileImageCropTitle(): string {
    return Number(this.profile?.role) === 4 ? 'Crop Profile Image' : 'Crop Organization Image';
  }

  get profileImageCropHint(): string {
    return Number(this.profile?.role) === 4
      ? 'Drag the image and adjust zoom to fit the profile area. The cropped image will be compressed before upload.'
      : 'Drag the image and adjust zoom to fit the organization logo area. The cropped image will be compressed before upload.';
  }

  get profileEditButtonLabel(): string {
    return this.canShowFullProfileEdit ? 'topHeader.editProfile' : 'topHeader.updatePassword';
  }

  get profileEditModalTitle(): string {
    return this.canShowFullProfileEdit ? 'topHeader.updateProfile' : 'topHeader.updatePassword';
  }

  private resetProfileModalState(): void {
    this.basicInfoMessage = null;
    this.basicInfoError = null;
    this.emailUpdateMessage = null;
    this.emailUpdateError = null;
    this.passwordUpdateMessage = null;
    this.passwordUpdateError = null;
    this.newEmailOtpExpiresInSeconds = 0;
    this.newEmailOtpTarget = '';
    this.isEmailOtpSent = false;
    this.showCurrentPassword = false;
    this.showNewPassword = false;
    this.showConfirmNewPassword = false;
    this.newEmailOtpCountdownSub?.unsubscribe();
  }

  private startNewEmailOtpCountdown(): void {
    this.newEmailOtpCountdownSub?.unsubscribe();
    this.newEmailOtpExpiresInSeconds = APP_OTP_EXPIRY_SECONDS;
    this.newEmailOtpCountdownSub = interval(1000).subscribe(() => {
      if (this.newEmailOtpExpiresInSeconds <= 1) {
        this.newEmailOtpExpiresInSeconds = 0;
        this.newEmailOtpCountdownSub?.unsubscribe();
        return;
      }
      this.newEmailOtpExpiresInSeconds -= 1;
    });
  }

  private formatCountdown(totalSeconds: number): string {
    if (!totalSeconds) {
      return '';
    }
    const min = Math.floor(totalSeconds / 60);
    const sec = totalSeconds % 60;
    return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}
